import type ApplicationController from "@mixitone/mvc";
import type { ControllerState } from "@mixitone/mvc";

/**
 * Toggle a state property on and off during the execution of the method
 */
export function withStateToggle<T extends ApplicationController<any, any, any>>(
  statePropertyKey: keyof ControllerState<T>,
  options: {
    returnIfTrue?: boolean;
    delayedOff?: number;
    errorPropertyKey?: keyof ControllerState<T>;
    queueIfRunning?: boolean;
  } = {},
) {
  return function (_target: T, _propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    // Map to track queued executions per instance
    const queuedExecutions = new WeakMap<T, boolean>();

    descriptor.value = async function (this: T, ...args: any[]) {
      // If already running and returnIfTrue is set
      if (options.returnIfTrue && this.state[statePropertyKey]) {
        // Queue a re-execution if queueIfRunning is true and no execution is already queued
        if (options.queueIfRunning && !queuedExecutions.get(this)) {
          queuedExecutions.set(this, true);
        }
        return;
      }

      // Store the arguments for potential re-execution
      const currentArgs = [...args];

      this.state[statePropertyKey] = true;
      try {
        const result = await originalMethod.apply(this, args);
        return result;
      } catch (error) {
        if (options.errorPropertyKey) {
          if (error instanceof Error) {
            this.state[options.errorPropertyKey] = error.message;
          } else {
            this.state[options.errorPropertyKey] = error;
          }
        } else {
          throw error;
        }
      } finally {
        const shouldReExecute = queuedExecutions.get(this);

        // Clear the queue flag immediately to prevent multiple re-executions
        queuedExecutions.set(this, false);

        if (options.delayedOff) {
          setTimeout(() => {
            this.state[statePropertyKey] = false;

            // Re-execute after the delay if queued
            if (shouldReExecute) {
              // We use the descriptor.value to ensure we're using the wrapped method
              descriptor.value.apply(this, currentArgs);
            }
          }, options.delayedOff);
        } else {
          this.state[statePropertyKey] = false;

          // Re-execute immediately if queued
          if (shouldReExecute) {
            // We use the descriptor.value to ensure we're using the wrapped method
            descriptor.value.apply(this, currentArgs);
          }
        }
      }
    };
  };
}
